import React, { useEffect, useState, forwardRef } from "react";
import { Input, Select, Button, Form, Card, InputNumber, message } from "antd";
import { X, PlusCircle } from "react-feather";
import { useSelector } from "react-redux";
import FeatherIcon from "feather-icons-react";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import {
  createList,
  updateList,
  createSegment,
  updateSegment,
  deleteSegment,
  getListById,
} from "../../core/api/mediaplan";

const ListDetailsView = forwardRef(
  (
    {
      isVisible,
      onClose,
      onResize,
      clientId,
      listId,
      setListOptions,
      onListCreated,
      onSegmentsUpdated,
    },
    ref
  ) => {
    const [form] = Form.useForm();
    const [segments, setSegments] = useState([]);
    const data = useSelector((state) => state.toggle_header);
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
      const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
      onResize(sidebarWidth);
    }, [isFullScreen, onResize]);

    const handleSubmit = async () => {
      try {
        await form.validateFields();
        const values = form.getFieldsValue();

        for (const segment of segments) {
          if (!segment.segment_code || !segment.segment_name) {
            throw new Error("Each segment must have a code and name.");
          }
        }

        let savedList;

        if (listId) {
          savedList = await updateList(listId, {
            code: values.list_code,
            name: values.list_name,
          });
        } else {
          savedList = await createList({
            code: values.list_code,
            name: values.list_name,
          });
          if (typeof onListCreated === "function") {
            onListCreated({
              id: savedList.data.id,
              name: savedList.data.name,
            });
          }
        }

        const savedListId = savedList.data.id;

        const updatedSegmentIds = new Set();

        for (const segment of segments) {
          if (segment.id) {
            const updatedSegment = await updateSegment(segment.id, {
              code: segment.segment_code,
              name: segment.segment_name,
              period: segment.period,
              quantity: segment.quantity,
              category: segment.category,
              list: savedListId,
              date_released: segment.date_released,
            });

            updatedSegmentIds.add(updatedSegment.data.id);
          } else {
            const newSegmentResponse = await createSegment({
              code: segment.segment_code,
              name: segment.segment_name,
              period: segment.period,
              quantity: segment.quantity,
              category: segment.category,
              list: savedListId,
              date_released: segment.date_released,
            });

            updatedSegmentIds.add(newSegmentResponse.data.id);
          }
        }

        const existingSegmentsResponse = await getListById(savedListId);
        const existingSegmentIds = new Set(
          existingSegmentsResponse.data.segments.map((s) => s.id)
        );

        const segmentsToDelete = [...existingSegmentIds].filter(
          (id) => !updatedSegmentIds.has(id)
        );

        for (const segmentId of segmentsToDelete) {
          await deleteSegment(segmentId);
        }

        message.success("List and segments saved successfully!");
        if (typeof onSegmentsUpdated === "function") {
          await onSegmentsUpdated(); 
        }
        form.resetFields();
        setSegments([]);
        onClose();
      } catch (error) {
        message.warning(error.message);
        console.error("Save failed:", error);
      }
    };

    const addSegment = () => {
      setSegments([
        ...segments,
        {
          segment_code: "",
          segment_name: "",
          period: "",
          quantity: 0,
          category: "",
          date_released: null,
        },
      ]);
    };

    const updateSegmentFunction = (index, key, value) => {
      setSegments((prevSegments) => {
        if (index < 0 || index >= prevSegments.length) {
          console.error(`Invalid index ${index} for segments array`);
          return prevSegments;
        }

        return prevSegments.map((segment, i) =>
          i === index
            ? { ...segment, [key]: key === "quantity" && value < 0 ? 0 : value }
            : segment
        );
      });
    };

    const removeSegment = async (index) => {
      const segment = segments[index];

      if (segment.id) {
        try {
          await deleteSegment(segment.id);
        } catch (error) {
          console.error("Failed to delete segment:", error);
        }
      }
      setSegments(segments.filter((_, i) => i !== index));
    };

    const columns = [
      {
        title: "Segment Code",
        dataIndex: "segment_code",
        width: "15%",
        align: "center",
        render: (_, record, index) => (
          <Input
            value={record.segment_code}
            onChange={(e) =>
              updateSegmentFunction(index, "segment_code", e.target.value)
            }
            placeholder="Enter Segment Code"
          />
        ),
      },
      {
        title: "Segment Name",
        dataIndex: "segment_name",
        align: "center",
        width: "15%",
        render: (_, record, index) => (
          <Input
            value={record.segment_name}
            onChange={(e) =>
              updateSegmentFunction(index, "segment_name", e.target.value)
            }
            placeholder="Enter Segment Name"
          />
        ),
      },
      {
        title: "Period",
        dataIndex: "period",
        width: "15%",
        align: "center",
        render: (_, record, index) => (
          <Input
            value={record.period}
            onChange={(e) =>
              updateSegmentFunction(index, "period", e.target.value)
            }
            placeholder="Enter Period"
          />
        ),
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        width: "15%",
        align: "center",
        render: (_, record, index) => (
          <InputNumber
            value={record.quantity}
            onChange={(value) =>
              updateSegmentFunction(index, "quantity", value)
            }
            placeholder="Enter Quantity"
            min={0}
          />
        ),
      },
      {
        title: "Category",
        dataIndex: "category",
        width: "15%",
        align: "center",
        render: (_, record, index) => (
          <Input
            value={record.category}
            onChange={(e) =>
              updateSegmentFunction(index, "category", e.target.value)
            }
            placeholder="Enter Category"
          />
        ),
      },
      {
        title: "Date Released",
        dataIndex: "date_released",
        width: "15%",
        align: "center",
        render: (_, record, index) => (
          <Input
            type="date"
            value={record.date_released}
            onChange={(e) =>
              updateSegmentFunction(index, "date_released", e.target.value)
            }
            placeholder="Select Date"
          />
        ),
      },
      {
        title: "Action",
        dataIndex: "action",
        width: "5%",
        render: (_, record, index) => (
          <Link onClick={() => removeSegment(index)}>
            <X className="x-square-add remove-product" />
          </Link>
        ),
      },
    ];

    useEffect(() => {
      const fetchListDetails = async () => {
        if (!isVisible || !listId) return;

        try {
          const response = await getListById(listId);
          const listData = response.data;

          form.setFieldsValue({
            list_code: listData.code,
            list_name: listData.name,
          });

          setSegments(
            listData.segments.map((segment) => ({
              id: segment.id,
              segment_code: segment.code,
              segment_name: segment.name,
              period: segment.period,
              quantity: segment.quantity,
              category: segment.category,
              date_released: segment.date_released || null,
            }))
          );
        } catch (error) {
          console.error("Error fetching list details:", error);
          message.error("Failed to fetch list details.");
        }
      };

      fetchListDetails();
    }, [listId, form, isVisible]);

    return (
      <div
        ref={ref}
        className={`right-sidebar ${isVisible ? "visible" : ""}`}
        style={{
          position: "fixed",
          top: data ? 0 : 66,
          right: 0,
          height: "100%",
          zIndex: 1050,
          width: isFullScreen ? "60%" : "555px",
          backgroundColor: "#fff",
          boxShadow: "0 0 3px rgba(0, 0, 0, 0.6)",
          overflowY: "auto",
          transition: "transform 0.3s ease-in-out",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
        }}>
        <div
          className="sidebar-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "15px",
            borderBottom: "1px solid #ddd",
          }}>
          <h5>{listId ? "Edit List" : "Add New List"}</h5>
          <div>
            <button
              type="link"
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="btn">
              <FeatherIcon icon={isFullScreen ? "minimize" : "maximize"} />
            </button>
            <button
              className="btn"
              onClick={() => {
                form.resetFields();
                setSegments([]);
                onClose();
              }}>
              <X />
            </button>
          </div>
        </div>

        {/* Form Section */}
        <div className="modal-body p-3">
          <Form form={form} layout="vertical">
            <Card title="List Details">
              <div
                className="mb-3"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}>
                <Form.Item
                  label="List Code"
                  name="list_code"
                  rules={[
                    { required: true, message: "List Code is required" },
                  ]}>
                  <Input placeholder="Enter List Code" />
                </Form.Item>

                <Form.Item
                  label="List Name"
                  name="list_name"
                  rules={[
                    { required: true, message: "List Name is required" },
                  ]}>
                  <Input placeholder="Enter List Name" />
                </Form.Item>
              </div>
            </Card>

            {/* Segments Section */}
            <Card title="Segments" className="mt-3">
              <div className="d-flex justify-content-end mb-2">
                <Button
                  type="primary"
                  className="btn btn-primary"
                  onClick={addSegment}>
                  <PlusCircle className="me-2 iconsize" />
                  Add Segment
                </Button>
              </div>
              <Table
                columns={columns}
                dataSource={segments}
                pagination={false}
              />
            </Card>

            <div className="d-flex justify-content-end mt-3">
              <Button
                type="primary"
                className="btn btn-primary me-2"
                onClick={handleSubmit}
                icon={<PlusCircle className="me-0 iconsize" />}>
                Save
              </Button>
              <Button
                type="default"
                className="btn btn-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  form.resetFields();
                  setSegments([]);
                  onClose();
                }}>
                Cancel
              </Button>
            </div>
            <div style={{ height: 100 }}></div>
          </Form>
        </div>
      </div>
    );
  }
);

ListDetailsView.displayName = "ListDetailsView";

export default ListDetailsView;
